import { Track } from '@bariendo/lib/constants';
import { PaymentStatus } from '@bariendo/lib/payment-status';
import { RefundHistoryEntry } from '@/services/Timeline/refundCards';

export interface BillingMenuRows {
  /** Opens the payment order drawer. */
  paymentRequest: boolean;
  /** Opens the patient's coverage form. */
  coverageActivity: boolean;
  /** Opens the refund or cancellation request (BAR-3069). */
  refundRequest: boolean;
}

/** Self-pay statuses with money in and nothing cancelled yet. */
const REFUNDABLE: ReadonlySet<PaymentStatus> = new Set([
  PaymentStatus.PARTIALLY_PAID,
  PaymentStatus.READY_TO_SCHEDULE,
  PaymentStatus.WON,
]);

/** Employer and insurance statuses with an approval and nothing cancelled yet. */
const CANCELLABLE: ReadonlySet<PaymentStatus> = new Set([PaymentStatus.READY_TO_SCHEDULE, PaymentStatus.WON]);

/**
 * Whether the patient can have a refund or cancellation request (BAR-3069), from the stored
 * payment status (BAR-3082): once a self-pay patient has paid, or an employer or insurance case
 * is approved. A cancelling request moves the status to Refund requested or Cancelled after
 * approval, which hides it; a kept-procedure refund leaves the status, and the row, where they were.
 *
 * The requests are read beside it: the restage after a request is best-effort, so a status still
 * reading Won must not offer a second cancellation. Unread or unreadable requests offer nothing.
 */
export function refundRequestAvailable(
  track: Track | undefined,
  status: PaymentStatus | undefined,
  requests: RefundHistoryEntry[] | undefined
): boolean {
  if (!status || !requests || requests.some((request) => request.cancel)) {
    return false;
  }
  return (track ?? Track.D2C) === Track.D2C ? REFUNDABLE.has(status) : CANCELLABLE.has(status);
}

/**
 * Which billing rows sit on + Add (BAR-3074). The patient's track decides; a row that
 * doesn't apply is hidden, never disabled.
 *
 * - Payment request: self-pay (no episode reads as self-pay, BAR-2941), before an order exists.
 * - Coverage activity: the employer tracks, an unverified employer included, whose Payor field
 *   names the administrator (BAR-3077), and insurance (BAR-3080).
 * - Refund or cancellation request: `refundRequestAvailable`, for a payments role. Not while the
 *   order loads, so the previous patient's order never offers it (BAR-3069).
 */
export function billingMenuRows(input: {
  canAccessPayments: boolean;
  paymentsFlagEnabled: boolean;
  canRecordCoverage: boolean;
  coverageFlagEnabled: boolean;
  track: Track | undefined;
  trackLoading: boolean;
  hasOrder: boolean;
  orderLoading: boolean;
  /** The payment status stored on the Patient (BAR-3082). */
  paymentStatus?: PaymentStatus;
  /** The patient's refund requests; undefined until read or when the read failed. */
  refundRequests?: RefundHistoryEntry[];
}): BillingMenuRows {
  const { track, trackLoading } = input;
  if (trackLoading) {
    return { paymentRequest: false, coverageActivity: false, refundRequest: false };
  }
  return {
    paymentRequest:
      input.canAccessPayments &&
      input.paymentsFlagEnabled &&
      (track ?? Track.D2C) === Track.D2C &&
      !input.orderLoading &&
      !input.hasOrder,
    coverageActivity:
      input.canRecordCoverage &&
      input.coverageFlagEnabled &&
      (track === Track.EMPLOYER || track === Track.CARRUM || track === Track.TRANSCARENT || track === Track.INSURANCE),
    refundRequest:
      input.canAccessPayments &&
      input.paymentsFlagEnabled &&
      !input.orderLoading &&
      // Self-pay refunds money on the open order: none read, nothing to refund from.
      ((track ?? Track.D2C) !== Track.D2C || input.hasOrder) &&
      refundRequestAvailable(track, input.paymentStatus, input.refundRequests),
  };
}
